import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CandidateCvUploadResponseDto, CandidateProfileDto, CandidateProfileUpdateRequestDto } from "../models/candidate-profile.model";
import { SendOtpRequestDto, SendOtpResponseDto, VerifyOtpRequestDto } from "../models/auth.model";
import { CandidateApplicationDetailDto, CandidateApplicationDto, CandidateCvResponseDto } from "../models/candidate-application.model";
import { environment } from "../../../../environments/environment";


/**
 * Candidate-portal API — pokriva /candidate-portal/* endpointe (profil, phone
 * verify, prijave, CV ops). Svi pozivi traže Bearer (authInterceptor attacha);
 * 401 handle-a refreshTokenInterceptor. Auth/login pozivi (/auth/*) žive u
 * AuthApiService.
 */
@Injectable({ providedIn: 'root' })
export class CandidatePortalApiService {
    private http: HttpClient = inject(HttpClient);
    private apiUrl = environment.apiBaseUrl;

    /**
     * POST /candidate-portal/phone/send-otp — pošalji kod za verifikaciju
     * telefona na profilu (NIJE login OTP — backend dostavlja preko WhatsAppa,
     * PhoneVerificationService). Zahtijeva hasRole('CANDIDATE'); Bearer attacha
     * authInterceptor. Dijeli request/response DTO-e s login OTP endpointima.
     */
    sendPhoneVerificationOtp(body: SendOtpRequestDto): Observable<SendOtpResponseDto> {
        return this.http.post<SendOtpResponseDto>(
            `${this.apiUrl}/candidate-portal/phone/send-otp`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * POST /candidate-portal/phone/verify-otp — provjeri kod; na uspjeh backend
     * sprema kanonski broj na User i setira phoneVerified=true (bez logina).
     */
    verifyPhoneVerificationOtp(body: VerifyOtpRequestDto): Observable<SendOtpResponseDto> {
        return this.http.post<SendOtpResponseDto>(
            `${this.apiUrl}/candidate-portal/phone/verify-otp`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-profile — full snapshot trenutnog candidate profila.
     * Bearer attacha authInterceptor; 401 handle-a refreshTokenInterceptor.
     */
    getMyProfile(): Observable<CandidateProfileDto> {
        return this.http.get<CandidateProfileDto>(
            `${this.apiUrl}/candidate-portal/my-profile`,
            { withCredentials: true },
        );
    }

    /**
     * PUT /candidate-portal/my-profile — update candidate profila.
     * Bearer attacha authInterceptor; 401 handle-a refreshTokenInterceptor.
     * withCredentials zbog refresh cookie-ja (konzistentno s ostalim pozivima).
     */
    updateProfile(body: CandidateProfileUpdateRequestDto): Observable<CandidateProfileDto> {
        return this.http.put<CandidateProfileDto>(
            `${this.apiUrl}/candidate-portal/my-profile`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-applications — lista svih candidate recorda
     * (prijava) trenutnog usera. Backend ne sortira; frontend sortira po
     * appliedAt desc. `id` u svakom item-u je candidate.id (Candidate tablica,
     * nije user.id) — koristi se za navigaciju na detail u koracima 8/9.
     */
    getMyApplications(): Observable<CandidateApplicationDto[]> {
        return this.http.get<CandidateApplicationDto[]>(
            `${this.apiUrl}/candidate-portal/my-applications`,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-applications/{id} — detail jedne prijave.
     * `id` je candidate.id. Vraća pun snapshot s answers, interviews, score
     * polja, CV state. Backend verificira da current user posjeduje candidate
     * record (401/403 ako pokuša tuđu prijavu).
     */
    getApplicationDetail(id: number): Observable<CandidateApplicationDetailDto> {
        return this.http.get<CandidateApplicationDetailDto>(
            `${this.apiUrl}/candidate-portal/my-applications/${id}`,
            { withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/my-applications/{id} — kandidat povlači prijavu.
     * Trajno: backend briše candidate redak sa svim ovisnim podacima (odgovori, CV,
     * termini, poruke) i gasi workflow. 204 na uspjeh, 403 ako prijava nije njegova,
     * 404 ako ne postoji.
     */
    withdrawApplication(candidateId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/my-applications/${candidateId}`,
            { withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/profile — trajno brisanje računa i SVIH prijava.
     * Backend traži lozinku samo za LOCAL račune (400 ako fali ili je kriva);
     * magic-link / Google / Facebook / OTP računi šalju `password: null`.
     * Body na DELETE-u: Angular HttpClient ga podržava kroz options.body.
     */
    deleteAccount(password: string | null): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/profile`,
            { body: { password }, withCredentials: true },
        );
    }

    /**
     * POST /candidate-portal/my-applications/{candidateId}/cv — multipart upload.
     * Request param: 'file'. Vraća puni CandidateCvResponseDto s GCS signed URL-om
     * za kasniji download. 201 Created na success.
     */
    uploadCv(candidateId: number, file: File): Observable<CandidateCvResponseDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<CandidateCvResponseDto>(
            `${this.apiUrl}/candidate-portal/my-applications/${candidateId}/cv`,
            formData,
            { withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/candidates/{candidateId}/cv — briše CV blob (GCS)
     * i candidate_cv row. Idempotentno: 204 i kad CV ne postoji.
     */
    deleteCv(candidateId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/candidates/${candidateId}/cv`,
            { withCredentials: true },
        );
    }

    /**
     * POST /candidate-portal/my-profile/cv — multipart upload profilnog CV-a
     * (jedan po useru; drugi upload pregazi prethodni). Request param: 'file'.
     * Content-Type NE postavljamo ručno — FormData sam doda multipart boundary.
     * Response `fileUrl` je potpisan-i-istekljiv GCS link → ignoriramo ga;
     * za download koristi downloadProfileCv(). 400 ako fali/krivi tip/prevelik.
     */
    uploadProfileCv(file: File): Observable<CandidateCvUploadResponseDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<CandidateCvUploadResponseDto>(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            formData,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-profile/cv — vraća same bajtove fajla
     * (responseType: 'blob'). Filename uzimamo iz profila (cvFileName), ne iz
     * Content-Disposition (cross-origin JS ga ne čita bez expose-headera).
     * 400 ako user nema CV → zovi samo kad je hasCv true.
     */
    downloadProfileCv(): Observable<Blob> {
        return this.http.get(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            { responseType: 'blob', withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/my-profile/cv — briše profilni CV.
     * Idempotentno: 204 i kad CV ne postoji.
     */
    deleteProfileCv(): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            { withCredentials: true },
        );
    }
}
